import { LOOP } from '../game/config';
import { AudioSystem } from '../audio/audio-system';
import {
  createSimulation,
  destroyPlayer,
  setSimulationPaused,
  spawnPrimaryThreat,
  startSimulation,
  updateSimulation,
} from '../game/simulation';
import type { ThreatKind } from '../game/models';
import { InputManager } from '../input/input-manager';
import { VectorRenderer } from '../render/renderer';
import {
  TankzoneStorage,
  sanitizeInitials,
  type ScoreEntry,
  type Settings,
} from '../storage/storage';

export class TankzoneApp {
  private readonly renderer: VectorRenderer;
  private readonly audio = new AudioSystem();
  private readonly scene = createSimulation(0x74a2c091);
  private readonly input: InputManager;
  private readonly storage = new TankzoneStorage(getLocalStorage());
  private readonly primaryPanel: HTMLElement;
  private readonly initialsDialog: HTMLDialogElement;
  private readonly initialsInput: HTMLInputElement;
  private readonly initialsOutput: HTMLOutputElement;
  private readonly initialsSubmit: HTMLButtonElement;
  private readonly leaderboard: HTMLOListElement;
  private readonly liveRegion: HTMLElement | null;
  private readonly reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  private settings: Settings;
  private scores: ScoreEntry[];
  private pendingInitials = '';
  private animationFrame = 0;
  private bootTimer = 0;
  private lastTime = 0;
  private accumulator = 0;
  private pausedByVisibility = false;
  private presentedMode = this.scene.mode;

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly overlay: HTMLElement,
    leftLever: HTMLButtonElement,
    rightLever: HTMLButtonElement,
    fireButton: HTMLButtonElement,
  ) {
    this.renderer = new VectorRenderer(canvas);
    this.primaryPanel = requiredChild(overlay, '#primary-panel');
    this.initialsDialog = requiredChild(document, '#initials-dialog');
    this.initialsInput = requiredChild(this.initialsDialog, '#initials-input');
    this.initialsOutput = requiredChild(this.initialsDialog, '#initials-output');
    this.initialsSubmit = requiredChild(this.initialsDialog, '#initials-submit');
    this.leaderboard = requiredChild(overlay, '#leaderboard-list');
    this.liveRegion = document.querySelector('#live-region');
    this.settings = this.storage.loadSettings();
    this.scores = this.storage.loadScores();
    this.input = new InputManager(
      leftLever,
      rightLever,
      fireButton,
      () => !['attract', 'game-over'].includes(this.scene.mode),
      this.pause,
      this.activateStartControl,
    );
  }

  public mount(): void {
    this.applySettings();
    this.renderLeaderboard();
    this.bootTimer = window.setTimeout(() => this.presentStandby(), 650);
    this.renderer.resize();
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.reducedMotionMedia.addEventListener('change', this.handleMotionPreference);
    this.input.mount();
    this.animationFrame = requestAnimationFrame(this.frame);
  }

  public start(): void {
    window.clearTimeout(this.bootTimer);
    void this.audio.unlock().catch(() => {
      // Browsers may reject Web Audio startup; gameplay remains fully usable without sound.
    });
    if (this.scene.mode === 'attract') startSimulation(this.scene);
    else if (this.scene.mode === 'game-over') startSimulation(this.scene);
    else if (this.scene.mode === 'paused') setSimulationPaused(this.scene, false);
    this.syncModeUi();
    this.syncPauseControls();
    this.audio.setPaused(false);
    this.showPrimaryPanel();
    this.overlay.classList.add('game-overlay--hidden');
    this.announce('Mission display active. Dual tread controls ready.');
    this.canvas.parentElement?.focus();
  }

  public isPaused(): boolean {
    return this.scene.mode === 'paused';
  }

  public toggleMuted(): boolean {
    this.settings.muted = this.audio.toggleMuted();
    this.storage.saveSettings(this.settings);
    return this.settings.muted;
  }

  public getSettings(): Readonly<Settings> {
    return { ...this.settings };
  }

  public updateSettings(update: Partial<Settings>): Settings {
    this.storage.saveSettings({ ...this.settings, ...update });
    this.settings = this.storage.loadSettings();
    this.applySettings();
    return { ...this.settings };
  }

  public pauseForDialog(): void {
    this.pause();
    this.input.clear();
  }

  public isEnteringInitials(): boolean {
    return this.initialsDialog.open;
  }

  public togglePause(): void {
    if (document.querySelector('#help-dialog[open], #settings-dialog[open]')) return;
    if (this.scene.mode === 'paused') this.start();
    else this.pause();
  }

  public setInitials(value: string): void {
    this.pendingInitials = value.toUpperCase().replace(/[^A-Z0-9.-]/g, '').slice(0, 3);
    this.updateInitialsDisplay();
  }

  public appendInitial(character: string): void {
    if (!this.isEnteringInitials() || this.pendingInitials.length >= 3 || !/^[A-Z0-9.-]$/i.test(character)) return;
    this.pendingInitials += character.toUpperCase();
    this.updateInitialsDisplay();
  }

  public backspaceInitial(): void {
    if (!this.isEnteringInitials()) return;
    this.pendingInitials = this.pendingInitials.slice(0, -1);
    this.updateInitialsDisplay();
  }

  public submitInitials(): boolean {
    if (!this.isEnteringInitials() || this.pendingInitials.length !== 3) {
      this.announce('Enter all three callsign characters before locking the score.');
      this.focusInitialsControl();
      return false;
    }
    const initials = sanitizeInitials(this.pendingInitials);
    this.scores = this.storage.addScore(initials, this.scene.score);
    this.renderLeaderboard();
    this.showPrimaryPanel();
    this.setPrimaryCopy(
      'SCORE RECORDED',
      `${initials} // ${String(this.scene.score).padStart(6, '0')}. Return to the grid?`,
      'NEW MISSION',
    );
    this.announce(`Score recorded for ${initials.split('').join(' ')}.`);
    this.overlay.querySelector<HTMLButtonElement>('#start-button')?.focus();
    return true;
  }

  public clearScores(): void {
    this.scores = this.storage.clearScores();
    this.renderLeaderboard();
    this.announce('Local scores restored to cabinet defaults.');
  }

  public testSnapshot(): ReturnType<NonNullable<Window['__TANKZONE_TEST__']>['snapshot']> {
    const audio = this.audio.debugSnapshot();
    return {
      mode: this.scene.mode,
      score: this.scene.score,
      tanks: this.scene.tanks,
      threat: this.scene.primaryThreat?.kind ?? null,
      hasSkimmer: this.scene.skimmer !== null,
      projectiles: this.scene.projectiles.length,
      audioVoices: audio.continuousVoices,
      audioMuted: audio.muted,
      audioPaused: audio.paused,
      audioMasterGain: audio.masterGain,
    };
  }

  public testCommand(name: string, value?: string | number): void {
    if (name === 'begin-playing') {
      if (this.scene.mode === 'attract' || this.scene.mode === 'game-over') startSimulation(this.scene);
      for (let index = 0; index < 190; index += 1) updateSimulation(this.scene, { leftTread: 0, rightTread: 0, firePressed: false }, LOOP.fixedStep);
    } else if (name === 'spawn-threat' && ['scout', 'missile', 'siege'].includes(String(value))) {
      this.scene.primaryThreat = null;
      this.scene.threatIntermission = 999;
      spawnPrimaryThreat(this.scene, value as ThreatKind);
    } else if (name === 'spawn-skimmer') {
      this.scene.skimmer = {
        id: this.scene.nextEntityId++,
        position: { x: 0, y: 2.8, z: 20 },
        previousPosition: { x: 0, y: 2.8, z: 20 },
        velocity: { x: 0, y: 0, z: 0 },
        radius: 1.45,
        age: 0,
      };
    } else if (name === 'scenario-skimmer-hit') {
      this.scene.player.position = { x: 0, y: 0, z: 0 };
      this.scene.player.yaw = 0;
      this.scene.spawnProtection = 0;
      this.scene.score = 0;
      this.scene.primaryThreat = null;
      this.scene.threatIntermission = 999;
      this.scene.skimmer = {
        id: this.scene.nextEntityId++,
        position: { x: 0, y: 2.8, z: 12 },
        previousPosition: { x: 0, y: 2.8, z: 12 },
        velocity: { x: 0, y: 0, z: 0 },
        radius: 1.45,
        age: 0,
      };
    } else if (name === 'scenario-missile-cover') {
      const obstacle = this.scene.obstacles[0];
      if (obstacle) {
        this.scene.score = 0;
        this.scene.player.position = { x: obstacle.position.x, y: 0, z: obstacle.position.z + 30 };
        this.scene.primaryThreat = {
          id: this.scene.nextEntityId++,
          kind: 'missile',
          position: { x: obstacle.position.x, y: 2.4, z: obstacle.position.z - 7 },
          previousPosition: { x: obstacle.position.x, y: 2.4, z: obstacle.position.z - 7 },
          yaw: 0,
          radius: 1.5,
          aiMode: 'seek',
          aiTime: 3,
          cooldown: 0,
          weavePhase: 0,
          flankDirection: 1,
        };
        this.scene.threatIntermission = 999;
      }
    } else if (name === 'set-tanks' && typeof value === 'number') {
      this.scene.tanks = Math.max(0, Math.floor(value));
    } else if (name === 'set-score' && typeof value === 'number') {
      this.scene.score = Math.max(0, Math.floor(value));
    } else if (name === 'destroy-player') {
      this.scene.spawnProtection = 0;
      destroyPlayer(this.scene);
    } else if (name === 'advance' && typeof value === 'number') {
      const steps = Math.ceil(Math.max(0, Math.min(10, value)) / LOOP.fixedStep);
      for (let index = 0; index < steps; index += 1) updateSimulation(this.scene, { leftTread: 0, rightTread: 0, firePressed: false }, LOOP.fixedStep);
    }
    this.syncModeUi();
  }

  public destroy(): void {
    cancelAnimationFrame(this.animationFrame);
    window.clearTimeout(this.bootTimer);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.reducedMotionMedia.removeEventListener('change', this.handleMotionPreference);
    this.input.destroy();
    void this.audio.destroy();
  }

  private readonly frame = (time: number): void => {
    if (this.pausedByVisibility) return;
    if (this.lastTime === 0) this.lastTime = time;
    const delta = Math.min((time - this.lastTime) / 1000, LOOP.maxFrameDelta);
    this.lastTime = time;
    this.accumulator += delta;
    let updates = 0;
    while (this.accumulator >= LOOP.fixedStep && updates < LOOP.maxUpdatesPerFrame) {
      this.update(LOOP.fixedStep);
      this.accumulator -= LOOP.fixedStep;
      updates += 1;
    }
    if (updates === LOOP.maxUpdatesPerFrame) this.accumulator = 0;
    this.renderer.render(this.scene);
    this.animationFrame = requestAnimationFrame(this.frame);
  };

  private update(delta: number): void {
    updateSimulation(this.scene, this.input.sample(), delta);
    this.audio.handleEvents(this.scene.events);
    this.audio.update(this.scene);
    this.syncModeUi();
  }

  private readonly pause = (): void => {
    if (!['ready', 'playing', 'life-lost'].includes(this.scene.mode)) return;
    setSimulationPaused(this.scene, true);
    this.audio.setPaused(true);
    this.input.clear();
    this.overlay.classList.remove('game-overlay--hidden');
    this.showPrimaryPanel();
    this.setPrimaryCopy('SYSTEM PAUSED', 'Simulation held. Treads returned to neutral.', 'RESUME MISSION');
    this.announce('System paused. Treads returned to neutral.');
    this.presentedMode = 'paused';
    this.syncPauseControls();
    this.overlay.querySelector<HTMLButtonElement>('#start-button')?.focus();
  };

  private readonly activateStartControl = (): void => {
    if (document.querySelector('dialog[open]')) return;
    if (this.scene.mode === 'attract' || this.scene.mode === 'game-over') this.start();
    else this.togglePause();
  };

  private syncModeUi(): void {
    this.syncPauseControls();
    if (this.scene.mode === this.presentedMode) return;
    this.presentedMode = this.scene.mode;
    if (this.scene.mode === 'game-over') {
      this.overlay.classList.remove('game-overlay--hidden');
      this.input.clear();
      if (this.storage.qualifies(this.scene.score)) this.presentInitialsEntry();
      else {
        this.showPrimaryPanel();
        this.setPrimaryCopy(
          'MISSION ENDED',
          `Final score ${String(this.scene.score).padStart(6, '0')}. Return to the grid?`,
          'NEW MISSION',
        );
        this.announce(`Mission ended. Final score ${this.scene.score}.`);
        this.overlay.querySelector<HTMLButtonElement>('#start-button')?.focus();
      }
      return;
    }
    if (this.scene.mode === 'ready') this.announce('Tank ready. Sensors stabilizing.');
    else if (this.scene.mode === 'life-lost') this.announce(`Tank destroyed. ${this.scene.tanks} reserves remain.`);
    if (this.scene.mode !== 'paused') this.overlay.classList.add('game-overlay--hidden');
  }

  private readonly handleResize = (): void => {
    this.renderer.resize();
  };

  private readonly handleVisibility = (): void => {
    this.lastTime = 0;
    this.accumulator = 0;
    if (document.hidden) {
      this.pause();
      this.pausedByVisibility = true;
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
      return;
    }
    if (this.pausedByVisibility) {
      this.pausedByVisibility = false;
      this.animationFrame = requestAnimationFrame(this.frame);
    }
  };

  private readonly handleMotionPreference = (): void => {
    if (this.settings.reducedMotion === 'system') this.applySettings();
  };

  private applySettings(): void {
    this.audio.setMuted(this.settings.muted);
    this.audio.setVolume(this.settings.volume);
    this.input.setPreset(this.settings.keyboardPreset);
    const faithful = this.settings.keyboardPreset === 'faithful';
    const presetCopy = faithful
      ? 'FAITHFUL // W/S LEFT · ↑/↓ RIGHT'
      : 'STANDARD // W/S MOVE · A/D TURN (ARROWS ALSO)';
    const presetHint = document.querySelector<HTMLElement>('#preset-hint');
    const controlPresetCopy = document.querySelector<HTMLElement>('#control-preset-copy');
    const treadDiagram = document.querySelector<HTMLElement>('#tread-diagram');
    const helpDriveCopy = document.querySelector<HTMLElement>('#help-drive-copy');
    if (presetHint) presetHint.textContent = presetCopy;
    if (controlPresetCopy) controlPresetCopy.textContent = presetCopy;
    if (treadDiagram) treadDiagram.hidden = !faithful;
    if (helpDriveCopy) {
      helpDriveCopy.textContent = faithful
        ? 'Use W/S for the left tread and ↑/↓ for the right. Match directions to move. Oppose them to pivot. One moving tread makes a slower arc.'
        : 'Use W/S to move and A/D to turn; the arrow keys provide the same standard steering. Space fires the cannon.';
    }
    this.input.setTouchMode(this.settings.touchMode);
    const reducedMotion = this.settings.reducedMotion === 'reduce'
      || (this.settings.reducedMotion === 'system' && this.reducedMotionMedia.matches);
    this.renderer.setReducedMotion(reducedMotion);
    document.documentElement.dataset.reducedMotion = String(reducedMotion);
  }

  private presentStandby(): void {
    if (this.scene.mode !== 'attract') return;
    this.setPrimaryCopy(
      'SYSTEM STANDBY',
      'Defend the grid. Destroy hostile armor. Survive as long as the vector holds.',
      'START MISSION',
    );
    this.announce('Tankzone system standby. Start mission when ready.');
  }

  private presentInitialsEntry(): void {
    this.primaryPanel.hidden = true;
    if (!this.initialsDialog.open) this.initialsDialog.showModal();
    this.focusInitialsControl();
    this.pendingInitials = '';
    this.updateInitialsDisplay();
    this.announce(`Local record qualified with ${this.scene.score} points. Enter a three character callsign.`);
    window.requestAnimationFrame(() => this.focusInitialsControl());
  }

  private focusInitialsControl(): void {
    document.querySelector<HTMLButtonElement>('#character-grid button')?.focus();
  }

  private showPrimaryPanel(): void {
    this.primaryPanel.hidden = false;
    if (this.initialsDialog.open) this.initialsDialog.close();
  }

  private updateInitialsDisplay(): void {
    const display = this.pendingInitials.padEnd(3, '.');
    this.initialsOutput.value = display;
    this.initialsOutput.textContent = display;
    this.initialsInput.value = this.pendingInitials;
    this.initialsSubmit.disabled = this.pendingInitials.length !== 3;
  }

  private setPrimaryCopy(title: string, copy: string, button: string): void {
    const titleElement = this.overlay.querySelector<HTMLElement>('#overlay-title');
    const copyElement = this.overlay.querySelector<HTMLElement>('#overlay-copy');
    const buttonElement = this.overlay.querySelector<HTMLButtonElement>('#start-button');
    if (titleElement) titleElement.textContent = title;
    if (copyElement) copyElement.textContent = copy;
    if (buttonElement) buttonElement.textContent = button;
  }

  private renderLeaderboard(): void {
    this.leaderboard.replaceChildren(...this.scores.map((entry, index) => {
      const item = document.createElement('li');
      const callsign = document.createElement('span');
      const score = document.createElement('b');
      callsign.textContent = `${index + 1}. ${entry.initials}`;
      score.textContent = String(entry.score).padStart(6, '0');
      item.append(callsign, score);
      return item;
    }));
    const record = this.scores[0]?.score ?? 0;
    this.renderer.setRecordScore(record);
    const recordElement = document.querySelector<HTMLElement>('#record-score');
    if (recordElement) recordElement.textContent = String(record).padStart(6, '0');
  }

  private announce(message: string): void {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      if (this.liveRegion) this.liveRegion.textContent = message;
    }, 20);
  }

  private syncPauseControls(): void {
    const paused = this.scene.mode === 'paused';
    for (const selector of ['#pause-button', '#touch-pause']) {
      const button = document.querySelector<HTMLButtonElement>(selector);
      if (!button) continue;
      button.textContent = paused ? 'RESUME' : 'PAUSE';
      button.setAttribute('aria-pressed', String(paused));
    }
  }
}

function requiredChild<T extends Element>(parent: Document | Element, selector: string): T {
  const element = parent.querySelector<T>(selector);
  if (!element) throw new Error(`Required child not found: ${selector}`);
  return element;
}

function getLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
